import { useMemo } from 'react';
import Decimal from 'decimal.js';
import { dateFormatter as getDateFormatter } from '@wix/crm-financial-formatters';
import { InvoiceStatus } from '@wix/ambassador-wix-quotes-web/types';
import { currencyFormat, dateFormat, localDate } from '../../core';
import { isStatusDisplayed } from '../InvoiceViewV2/Status/status-utils';
import { hasDeposit } from '../DocParts/Totals/Deposit/hasDeposit';
import { ORDER_REFERENCE_CUSTOM_FIELD_KEY } from '../DocParts/InvoiceInfo/OrderReference';
import { mapInvoiceDetailsFromDTO } from './utils';
export const useMobileInvoiceView = ({ businessCustomFields, customFields, customInvoiceNumberLabel, documentCustomFields, footer, invoice, primarySiteLocale, recurringInvoiceViewEnabled, status, t, }) => {
    const invoiceDetails = useMemo(() => mapInvoiceDetailsFromDTO({
        invoice,
        recurringInvoiceViewEnabled,
        status,
    }), [invoice, recurringInvoiceViewEnabled, status]);
    const moneyFormatter = useMemo(() => currencyFormat({
        currency: invoiceDetails.currency,
        locale: invoiceDetails.locale,
    }), [invoiceDetails.currency, invoiceDetails.locale]);
    const dateFormatter = useMemo(() => primarySiteLocale
        ? getDateFormatter(primarySiteLocale, true)
        : localDate(dateFormat({ locale: invoiceDetails.locale })), [invoiceDetails.locale, primarySiteLocale]);
    const invoiceSummaryProps = {
        balance: invoiceDetails.balance,
        deposit: invoiceDetails.deposit,
        discount: invoiceDetails.discount,
        fees: invoiceDetails.fees,
        paid: invoiceDetails.paid,
        subtotal: invoiceDetails.subtotal,
        taxes: invoiceDetails.taxes,
        total: invoiceDetails.total,
        taxedTotal: invoiceDetails.taxedTotal,
    };
    const footerNotesProps = {
        footer: {
            firstLabel: footer?.notesLabel,
            secondLabel: footer?.legalTermsLabel,
        },
        metaData: invoiceDetails.metaData,
    };
    const hasValidDeposit = hasDeposit({
        depositAmount: invoiceDetails.deposit.amount,
        paidAmount: invoiceDetails.paid,
    });
    const dueAmount = hasValidDeposit
        ? Decimal.sub(invoiceDetails.deposit.amount, invoiceDetails.paid).toNumber()
        : invoiceDetails.balance;
    const showInvoiceHighlight = invoiceDetails.status !== InvoiceStatus.Paid;
    const showInvoiceStatus = isStatusDisplayed({ status, isUoU: true });
    const orderReferenceCustomField = customFields?.find(({ originCustomFieldKey }) => originCustomFieldKey === ORDER_REFERENCE_CUSTOM_FIELD_KEY);
    const orderReference = orderReferenceCustomField?.value
        ? t('invoice-view.order-number.label', {
            orderNumber: orderReferenceCustomField.value.value ?? '',
        })
        : undefined;
    const invoiceNumberLabel = customInvoiceNumberLabel
        ? t('invoice_view.custom-invoice-number-label', {
            customInvoiceNumberLabel,
            number: invoiceDetails.invoiceNumber,
        })
        : t('invoice_view.invoice_number', {
            number: invoiceDetails.invoiceNumber,
        });
    const documentCustomFieldsContent = useMemo(() => documentCustomFields
        ?.filter(({ value, displayName }) => !!value && !!displayName)
        .map(({ displayName, value }) => t('price-quote.view.custom-field', {
        displayName,
        value,
    })) ?? [], [documentCustomFields, t]);
    const businessDetailsCustomFieldsContent = useMemo(() => businessCustomFields
        ?.filter(({ value, displayName }) => !!value && !!displayName)
        .map(({ displayName, value }) => t('price-quote.view.custom-field', {
        displayName,
        value,
    })) ?? [], [businessCustomFields, t]);
    return {
        businessDetailsCustomFieldsContent,
        dateFormatter,
        documentCustomFieldsContent,
        dueAmount,
        footerNotesProps,
        invoiceDetails,
        invoiceNumberLabel,
        invoiceSummaryProps,
        moneyFormatter,
        orderReference,
        showInvoiceHighlight,
        showInvoiceStatus,
    };
};
//# sourceMappingURL=useHooks.js.map